import { useCallback, useEffect, useRef, useState } from "react";

import history, { registerTransitionConfirmation } from "data/history";

import { WORK_DESK_EXIT_MS } from "./useWorkDeskScreen";
import { useWorkDeskUrl } from "./useWorkDeskUrl";

// replay the click a little after the work-desk exit finishes, so the
// control opens over the restored workspace rather than the still-leaving
// work desk. WORK_DESK_EXIT_MS is read at call time to avoid a module
// load-order issue with the hooks<->Container import cycle.
const EXIT_REPLAY_BUFFER_MS = 100;

export function useWorkSessionExitGuard({ active, atWorkDeskRoute, onExit }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingRef = useRef(null);
  const replayTimerRef = useRef();
  const { embedsClientCardIframe } = useWorkDeskUrl();

  const takePending = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    return pending;
  }, []);

  const cancelPending = useCallback(() => {
    setConfirmOpen(false);
    const pending = takePending();
    if (pending) pending.cancel();
  }, [takePending]);

  const confirmPending = useCallback(() => {
    setConfirmOpen(false);
    const pending = takePending();
    if (pending) pending.exit();
  }, [takePending]);

  const requestConfirmation = useCallback(
    (pending) => {
      cancelPending();
      pendingRef.current = pending;
      setConfirmOpen(true);
    },
    [cancelPending]
  );

  useEffect(() => {
    return () => {
      clearTimeout(replayTimerRef.current);
      const pending = takePending();
      if (pending) pending.cancel();
    };
  }, [takePending]);

  useEffect(() => {
    if (!active) cancelPending();
  }, [active, cancelPending]);

  useEffect(() => {
    if (!active || !atWorkDeskRoute) return undefined;

    const unblock = history.block(({ pathname }) => {
      if (embedsClientCardIframe(pathname)) return undefined;
      return "leave-work-session";
    });
    const unregister = registerTransitionConfirmation((respond) => {
      requestConfirmation({
        exit: () => {
          return respond(true);
        },
        cancel: () => {
          return respond(false);
        },
      });
    });

    return () => {
      unblock();
      unregister();
    };
  }, [active, atWorkDeskRoute, embedsClientCardIframe, requestConfirmation]);

  const exitSessionAndReplay = useCallback(
    (target) => {
      onExit();
      clearTimeout(replayTimerRef.current);
      replayTimerRef.current = setTimeout(() => {
        // the control can unmount while the exit settles (e.g. view buttons
        // disappear when insights returns to the front) - a click on a
        // detached node would silently no-op, so degrade to doing nothing
        if (target?.isConnected) target.click();
      }, WORK_DESK_EXIT_MS + EXIT_REPLAY_BUFFER_MS);
    },
    [onExit]
  );

  const interceptWorkSessionExit = useCallback(
    (e) => {
      if (!active) return;
      // portal-rendered popups/modals of wrapped controls bubble through
      // the React tree but live outside the guard's DOM subtree - let
      // their internal clicks through
      if (!e.currentTarget.contains(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      // snapshot the actual control, not the innermost icon/label node,
      // so the replay survives inner re-renders
      const target = e.target.closest("button") || e.target;
      requestConfirmation({
        exit: () => {
          return exitSessionAndReplay(target);
        },
        cancel: () => {},
      });
    },
    [active, requestConfirmation, exitSessionAndReplay]
  );

  return {
    interceptWorkSessionExit: interceptWorkSessionExit,
    exitConfirmOpen: confirmOpen,
    stayInSession: cancelPending,
    confirmExit: confirmPending,
  };
}

export default useWorkSessionExitGuard;
